import { ROLES_AND_RESPONSIBILITIES_ITEMS } from "../../components/Application/rolesAndResponsibilitiesConstants";
import {
  PROTOCOL_DOCUMENT_PRINT_STYLES,
  escapeHtml,
  formatDate,
  parseJobDescriptionFromApiResponse,
  protocolItemToHtml,
  signatureBlock,
} from "./jobDescriptionPrint";

/**
 * Same API envelope as {@link parseJobDescriptionFromApiResponse}
 * (`result.rolesAndResponsibilities`, `jobDescription`, `data`, list, etc.).
 */
export const parseRolesAndResponsibilitiesFromApiResponse =
  parseJobDescriptionFromApiResponse;

/**
 * Print HTML for Roles and Responsibilities (Exhibit A/B body from constants + signatures).
 * @param {Record<string, unknown>} record
 * @param {{ companyName: string; logoUrl: string; seniorStaffNurse?: string }} branding
 */
export function buildRolesAndResponsibilitiesPrintHtml(record, branding) {
  const company = escapeHtml(branding.companyName || "Company");
  const logoUrl = escapeHtml(branding.logoUrl || "");
  const seniorStaffNurse = branding.seniorStaffNurse || "";

  const bodyHtml = ROLES_AND_RESPONSIBILITIES_ITEMS.map((it) =>
    protocolItemToHtml(it, { ...branding, seniorStaffNurse }),
  ).join("");

  const displayName = escapeHtml(
    String(record.applicantName || record.name || "").trim(),
  );
  const roleDisp = escapeHtml(
    String(record.role || "").trim() || "—",
  );

  const es = record.employeeSignature;
  const nestedEmpSig =
    es && typeof es === "object"
      ? /** @type {{ signature?: string }} */ (es).signature
      : null;
  const applicantSig =
    record.applicantSignature ||
    record.empSignature ||
    nestedEmpSig;
  const applicantSignedAt = escapeHtml(
    formatDate(
      record.applicantSignedAt ||
        record.applicantSignatureDate ||
        record.empSignedAt,
    ),
  );
  const reviewedBySignedBy = escapeHtml(String(record.reviewedBySignedBy || ""));
  const reviewedBySig = record.reviewedBySignature || record.adminSignature;
  const reviewedBySignedAt = escapeHtml(
    formatDate(
      record.reviewedBySignedAt ||
        record.adminSignatureDate ||
        record.reviewedBySignatureDate,
    ),
  );

  const statementAccepted = !!record.applicantStatementAccepted;
  const statusCompleted =
    String(record.status || "").toLowerCase() === "completed";
  const showCertChecked = statementAccepted || statusCompleted;

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <title>Roles and responsibilities (${roleDisp}) — ${displayName || "Employee"}</title>
  <style>${PROTOCOL_DOCUMENT_PRINT_STYLES}</style>
</head>
<body>
  <div class="sheet">
    <div class="top">
      <img class="logo" src="${logoUrl}" alt="" onerror="this.style.display='none'" />
      <div class="title-block">
        <p class="title-main">Roles and responsibilities</p>
        <p class="title-sub">Role: ${roleDisp} · ${company}</p>
      </div>
    </div>

    ${bodyHtml}

    <div class="cert-box">
      <p class="cert-title">Applicant certification</p>
      <p class="cert-sub">Do not sign until you have read the above applicant statement.</p>
      <div class="cert-row">
        <input type="checkbox" class="chk" ${showCertChecked ? "checked" : ""} disabled />
        <span>I certify that I have read, fully understand and accept all terms of the foregoing Applicant Statement. <span style="color:#dc2626">*</span></span>
      </div>
    </div>

    <div class="two-col">
      <div class="sig-card">
        <h3>Employee</h3>
        <div class="sig-label">Name</div>
        <div class="field-value">${displayName || "—"}</div>
        <div class="sig-label">Signature</div>
        <div class="sig-line">${signatureBlock(applicantSig)}</div>
        <div class="sig-label">Date</div>
        <div class="sig-line">${applicantSignedAt || "—"}</div>
      </div>
      <div class="sig-card">
        <h3>Admin / reviewer</h3>
        <div class="sig-label">Name</div>
        <div class="field-value">${reviewedBySignedBy || "—"}</div>
        <div class="sig-label">Signature</div>
        <div class="sig-line">${signatureBlock(reviewedBySig)}</div>
        <div class="sig-label">Date</div>
        <div class="sig-line">${reviewedBySignedAt || "—"}</div>
      </div>
    </div>

    <div class="footer-print">${company}</div>
  </div>
</body>
</html>`;
}
